import { useState, useEffect } from 'react';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/transitional/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { captureException, toError } from '../../internal/ravenUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export function InitWrapper({
  appId,
  portalId,
  onInit,
  onStatusChange,
  children
}) {
  const [initState, setInitState] = useState(onInit ? {
    status: 'loading'
  } : {
    status: 'ready',
    resolvedProps: {}
  });
  useEffect(() => {
    if (!onInit) return;
    let isMounted = true;
    const runInit = async () => {
      try {
        setInitState({
          status: 'loading'
        });
        const resolvedProps = await onInit({
          appId,
          portalId
        });
        if (isMounted) {
          setInitState({
            status: 'ready',
            resolvedProps
          });
          onStatusChange === null || onStatusChange === void 0 || onStatusChange('ready');
        }
      } catch (err) {
        captureException(new Error('onInit hook failed', {
          cause: toError(err)
        }), {
          extra: {
            appId,
            portalId
          }
        });
        if (isMounted) {
          const message = err instanceof Error ? err.message : 'Failed to initialize component';
          setInitState({
            status: 'error',
            message
          });
          onStatusChange === null || onStatusChange === void 0 || onStatusChange('error', message);
        }
      }
    };
    void runInit();
    return () => {
      isMounted = false;
    };
    // onStatusChange is intentionally excluded: callers pass it as a fresh
    // closure each render, and including it would re-run onInit on every
    // render instead of once per appId/portalId/onInit change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, portalId, onInit]);
  if (initState.status === 'loading') {
    if (onStatusChange) return null;
    return /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      justifyContent: "center",
      gap: "100",
      paddingBlock: "300",
      children: [/*#__PURE__*/_jsx(LoadingSpinner, {}), /*#__PURE__*/_jsx(Text, {
        children: "Loading..."
      })]
    });
  }
  if (initState.status === 'error') {
    if (onStatusChange) return null;
    return /*#__PURE__*/_jsx(Alert, {
      type: "danger",
      titleText: "Initialization Error",
      children: /*#__PURE__*/_jsx(View, {
        paddingBlock: "200",
        paddingInline: "200",
        children: /*#__PURE__*/_jsx(Text, {
          children: initState.message
        })
      })
    });
  }
  if (initState.status === 'ready') {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children(initState.resolvedProps)
    });
  }
  return null;
}